import { useCallback, useMemo } from "react";

import type { Order } from "~/v2/api/rest/account";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import {
  useOrdersCancellationController,
  useOrdersCancellationState,
} from "./OrdersCancellation.controller";
import { useOrdersDisplayMenuController } from "./OrdersDisplayMenu.controller";
import { useOrdersFiltersController } from "./OrdersFilters.controller";
import {
  createOrdersWidgetColumns,
  getOrdersWidgetColumnOptions,
  getOrdersWidgetTableMinWidth,
  type OrdersWidgetColumnId,
  orderOrdersWidgetColumnOptions,
  selectVisibleOrdersWidgetColumns,
} from "./OrdersWidget.columns";
import type {
  OrdersWidgetRow,
  OrdersWidgetStatusFilter,
} from "./OrdersWidget.types";
import {
  collectOrderAssetNameSymbols,
  mapOrderToLinkedRows,
} from "./OrdersWidget.utils";
import {
  useOrdersWidgetDialogsController,
  useOrdersWidgetDialogsState,
} from "./OrdersWidgetDialogs.controller";
import { useAdvancedInstructionsAllowed } from "./useAdvancedInstructionsAllowed";
import { useAtdOrders } from "./useAtdOrders";
import { useOrderAssetNames } from "./useOrderAssetNames";
import { useOrdersColumnVisibility } from "./useOrdersColumnVisibility";
import { useOrdersManualRefresh } from "./useOrdersManualRefresh";

const ORDERS_PAGE_SIZE = 20;

export interface OrdersWidgetScope {
  symbol: string;
  matchesOrderSymbol: (orderSymbol: string) => boolean;
}

export interface OrdersWidgetProps {
  surface?: "standalone" | "workspace";
  scope?: OrdersWidgetScope;
  /** Mount-time and Reset default. Standalone remains "all". */
  defaultStatus?:
    | OrdersWidgetStatusFilter
    | readonly OrdersWidgetStatusFilter[];
  allowCancel?: boolean;
  pollingInterval?: number | false;
}

const buildOrdersWidgetRows = ({
  orders,
  scope,
  allowCancel,
  cancellingIds,
  replacementPendingOrderID,
  assetNames,
}: {
  orders: Order[];
  scope?: OrdersWidgetScope;
  allowCancel: boolean;
  cancellingIds: readonly string[];
  replacementPendingOrderID?: string;
  assetNames?: ReadonlyMap<string, string>;
}): OrdersWidgetRow[] =>
  orders.flatMap((order) => {
    const linkedRows = mapOrderToLinkedRows(order, {
      allowCancel,
      allowEdit: allowCancel,
      isCancelling: cancellingIds.includes(order.id),
      isEditing: replacementPendingOrderID === order.id,
      assetNames,
    });
    const parent = linkedRows[0];
    if (
      !parent ||
      (scope &&
        !scope.matchesOrderSymbol(order.symbol ?? "") &&
        !scope.matchesOrderSymbol(parent.rawSymbol) &&
        !linkedRows.some((row) => scope.matchesOrderSymbol(row.rawSymbol)))
    ) {
      return [];
    }
    return linkedRows;
  });

export const useOrdersWidgetController = ({
  surface = "standalone",
  scope,
  defaultStatus = "all",
  allowCancel = false,
  pollingInterval = false,
}: OrdersWidgetProps = {}) => {
  const {
    currentAccount,
    product,
    isLoading: isAccountLoading,
  } = useAccountContext();
  const filtersController = useOrdersFiltersController({
    defaultStatus,
    isScoped: Boolean(scope),
  });
  const accountID = currentAccount?.id;
  const { allowed: allowAdvancedInstructions } =
    useAdvancedInstructionsAllowed(accountID);
  const columnVisibility = useOrdersColumnVisibility(
    undefined,
    allowCancel,
    allowAdvancedInstructions,
  );
  const tableDisplay = useOrdersDisplayMenuController();

  const isAccountReady = Boolean(accountID && product);
  const selectionScopeKey = JSON.stringify({
    accountID,
    product,
    appliedFilters: filtersController.appliedFilters,
    scopeSymbol: scope?.symbol,
  });

  const ordersQuery = useAtdOrders({
    accountID,
    product,
    pageSize: ORDERS_PAGE_SIZE,
    symbols: filtersController.symbols,
    status: filtersController.status,
    after: filtersController.after,
    until: filtersController.until,
    timeWindow: filtersController.timeWindow,
    assetClass: filtersController.assetClass,
    orderType: filtersController.orderType,
    direction: filtersController.direction,
    enabled: isAccountReady,
    pollingInterval,
  });
  const { refresh, isRefreshDisabled, isRefreshing } = useOrdersManualRefresh({
    refetch: ordersQuery.refetch,
    isFetching: ordersQuery.isFetching,
    enabled: isAccountReady,
  });

  const assetNameSymbols = useMemo(
    () => collectOrderAssetNameSymbols(ordersQuery.orders),
    [ordersQuery.orders],
  );
  const assetNames = useOrderAssetNames(assetNameSymbols);

  const cancellationState = useOrdersCancellationState({
    accountID,
    product,
    selectionScopeKey,
  });
  const dialogsState = useOrdersWidgetDialogsState({
    accountID,
    product,
  });

  const rows = useMemo(
    () =>
      buildOrdersWidgetRows({
        orders: ordersQuery.orders,
        scope,
        allowCancel,
        cancellingIds: cancellationState.cancellingIds,
        replacementPendingOrderID: dialogsState.replacementPendingOrderID,
        assetNames,
      }),
    [
      allowCancel,
      assetNames,
      cancellationState.cancellingIds,
      dialogsState.replacementPendingOrderID,
      ordersQuery.orders,
      scope,
    ],
  );
  const rowCount = rows.filter((row) => !row.isLegRow).length;

  const cancellation = useOrdersCancellationController({
    state: cancellationState,
    rows,
    allowCancel,
    accountID,
    product,
  });
  const dialogs = useOrdersWidgetDialogsController({
    state: dialogsState,
    rows,
    allowCancel,
    accountID,
    product,
    setCancellationFeedback: cancellation.setCancellationFeedback,
    cancellingIdSetRef: cancellation.cancellingIdSetRef,
  });

  const allColumns = useMemo(
    () =>
      createOrdersWidgetColumns({
        onCancel: allowCancel ? cancellation.handleCancel : undefined,
        onEdit: allowCancel ? dialogs.handleEdit : undefined,
        selection: allowCancel
          ? {
              selectedIds: cancellation.selectedIdSet,
              onToggle: cancellation.handleToggleSelected,
              allSelected: cancellation.allCancellableSelected,
              someSelected: cancellation.someCancellableSelected,
              onToggleAll: cancellation.handleSelectAllCancellable,
            }
          : undefined,
      }),
    [
      cancellation.allCancellableSelected,
      allowCancel,
      cancellation.handleCancel,
      dialogs.handleEdit,
      cancellation.handleSelectAllCancellable,
      cancellation.handleToggleSelected,
      cancellation.selectedIdSet,
      cancellation.someCancellableSelected,
    ],
  );
  const columnOptions = useMemo(
    () =>
      orderOrdersWidgetColumnOptions(
        getOrdersWidgetColumnOptions(allowCancel, allowAdvancedInstructions),
        columnVisibility.columnOrder,
      ),
    [allowAdvancedInstructions, allowCancel, columnVisibility.columnOrder],
  );
  const visibleColumns = useMemo(
    () =>
      selectVisibleOrdersWidgetColumns(
        columnVisibility.visibleColumnIds,
        allColumns,
      ),
    [allColumns, columnVisibility.visibleColumnIds],
  );
  const tableMinWidth = useMemo(
    () => getOrdersWidgetTableMinWidth(visibleColumns),
    [visibleColumns],
  );

  const handleRetry = useCallback(() => {
    void ordersQuery.refetch();
  }, [ordersQuery.refetch]);

  return {
    assetSearch: scope
      ? null
      : {
          value: filtersController.symbolQuery,
          onChange: filtersController.onSymbolQueryChange,
          onSelect: filtersController.onSymbolSelect,
        },
    filters: {
      surface,
      filters: filtersController.filters,
      formKey: filtersController.formKey,
      validationError: filtersController.validationError,
      onFiltersChange: filtersController.onFiltersChange,
      onStatusTabChange: filtersController.onStatusTabChange,
      onReset: filtersController.onReset,
      rowCount,
      dataUpdatedAt: ordersQuery.dataUpdatedAt,
      onRefresh: refresh,
      isRefreshDisabled,
      isRefreshing,
    },
    columnsPopover: {
      columnOptions,
      visibleColumnIds: columnVisibility.visibleColumnIds,
      requiredColumnIds: allowCancel
        ? (["symbol", "action"] satisfies OrdersWidgetColumnId[])
        : (["symbol"] satisfies OrdersWidgetColumnId[]),
      onColumnVisibilityChange: columnVisibility.setColumnVisibility,
      onMoveColumn: columnVisibility.moveColumn,
      onReorderColumns: columnVisibility.reorderColumns,
      onSelectAllColumns: columnVisibility.selectAllColumns,
      onResetColumns: columnVisibility.resetColumnVisibility,
    },
    displayMenu: {
      density: tableDisplay.density,
      horizontalLines: tableDisplay.horizontalLines,
      verticalLines: tableDisplay.verticalLines,
      onDensityChange: tableDisplay.onDensityChange,
      onHorizontalLinesChange: tableDisplay.onHorizontalLinesChange,
      onVerticalLinesChange: tableDisplay.onVerticalLinesChange,
    },
    cancellation: {
      selectedCount: cancellation.selectedIds.length,
      cancellationFeedback: cancellation.cancellationFeedback,
      isBatchCancelling: cancellation.isBatchCancelling,
      isWorkspaceSurface: surface === "workspace",
      onClearSelected: cancellation.handleClearSelected,
      onRequestBatchCancel: allowCancel
        ? cancellation.handleRequestBatchCancel
        : undefined,
      setCancellationFeedback: cancellation.setCancellationFeedback,
    },
    replacementFeedback: {
      contained: surface === "workspace",
      message: dialogs.replacementFeedback,
      setReplacementFeedback: dialogs.setReplacementFeedback,
    },
    presentation: {
      surface,
      rows,
      columns: visibleColumns,
      tableMinWidth,
      resultsRegionRef: dialogs.resultsRegionRef,
      rowCount,
      selectedOrderId: dialogs.selectedOrderId,
      batchSelectedIds: cancellation.selectedIds,
      density: tableDisplay.density,
      horizontalLines: tableDisplay.horizontalLines,
      verticalLines: tableDisplay.verticalLines,
      isAccountReady,
      isLoading: isAccountLoading || (isAccountReady && ordersQuery.isLoading),
      isFetching: isAccountReady && ordersQuery.isFetching,
      isError: isAccountReady && ordersQuery.isError,
      isRefreshError: isAccountReady && ordersQuery.isRefetchError,
      errorMessage:
        ordersQuery.error instanceof Error
          ? ordersQuery.error.message
          : undefined,
      hasAppliedFilters: filtersController.hasAppliedFilters,
      appliedTimeWindow: filtersController.appliedTimeWindow,
      hasNextPage: ordersQuery.hasNextPage,
      isFetchingNextPage:
        isAccountReady && Boolean(ordersQuery.isFetchingNextPage),
      onReset: filtersController.onReset,
      onRetry: handleRetry,
      onLoadMore: ordersQuery.nextPage,
      onRowActivate: dialogs.handleRowActivate,
      onReorderColumns: columnVisibility.reorderColumns,
    },
    detailDialogProps: dialogs.detailDialogProps,
    replacementDialogProps: dialogs.replacementDialogProps,
  };
};
